import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, Menu, X,
  Calendar, CalendarPlus, Clock, MapPin, Eye, Trash2,
} from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import type { CalendarItem } from '@pms/shared';
import { calendarService, type CreateCalendarEventInput } from '@/services/calendar.service';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

// ─── Date helpers ─────────────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfWeek(d: Date): Date {
  const day = new Date(d);
  day.setDate(day.getDate() - day.getDay());
  day.setHours(0, 0, 0, 0);
  return day;
}

function endOfWeek(d: Date): Date {
  const day = new Date(d);
  day.setDate(day.getDate() + (6 - day.getDay()));
  day.setHours(23, 59, 59, 999);
  return day;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatMonthYear(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatWeekRange(d: Date): string {
  const s = startOfWeek(d);
  const e = endOfWeek(d);
  if (s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()} – ${e.getDate()}, ${s.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function formatDayFull(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
}

function itemsForDay(items: CalendarItem[], date: Date): CalendarItem[] {
  const dayStr = toDateStr(date);
  return items.filter((item) => {
    const start = item.start.slice(0, 10);
    const end   = item.end.slice(0, 10);
    return start <= dayStr && end >= dayStr;
  });
}

// ─── View type ───────────────────────────────────────────────────────────────

type CalendarView = 'day' | 'week' | 'month' | 'agenda';

// ─── Date range for query ────────────────────────────────────────────────────

function getQueryRange(view: CalendarView, date: Date): { start: Date; end: Date } {
  if (view === 'day')    { const s = new Date(date); s.setHours(0,0,0,0); const e = new Date(date); e.setHours(23,59,59,999); return { start: s, end: e }; }
  if (view === 'week')   return { start: startOfWeek(date), end: endOfWeek(date) };
  if (view === 'month')  return { start: addDays(startOfMonth(date), -7), end: addDays(endOfMonth(date), 7) };
  // agenda: next 60 days
  const today = new Date(); today.setHours(0,0,0,0);
  return { start: today, end: addDays(today, 60) };
}

// ─── Time grid constants ──────────────────────────────────────────────────────

const HOUR_HEIGHT = 64; // px per hour
const TOTAL_HEIGHT = 24 * HOUR_HEIGHT;

function minuteOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

// ─── Event chip ───────────────────────────────────────────────────────────────

function EventChip({ item, onClick, compact = false }: {
  item: CalendarItem;
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        'block w-full text-left rounded px-1.5 text-white text-xs font-medium truncate',
        compact ? 'py-0.5' : 'py-1',
        'hover:opacity-90 transition-opacity cursor-pointer',
      )}
      style={{ backgroundColor: item.color }}
      title={item.title}
    >
      {item.title}
    </button>
  );
}

// ─── Month View ───────────────────────────────────────────────────────────────

function MonthView({ currentDate, items, onDayClick, onEventClick }: {
  currentDate: Date;
  items: CalendarItem[];
  onDayClick: (d: Date) => void;
  onEventClick: (item: CalendarItem) => void;
}) {
  const today = new Date();
  const firstDay = startOfMonth(currentDate);
  const startGrid = startOfWeek(firstDay);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(startGrid, i));

  const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Day labels */}
      <div className="grid grid-cols-7 border-b border-border">
        {DAY_LABELS.map((d) => (
          <div key={d} className="py-2 text-center text-[11px] font-semibold text-muted-foreground tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 grid grid-cols-7 grid-rows-6 overflow-hidden">
        {cells.map((cell, i) => {
          const isCurrentMonth = cell.getMonth() === currentDate.getMonth();
          const isToday = sameDay(cell, today);
          const dayItems = itemsForDay(items, cell);
          const visible = dayItems.slice(0, 3);
          const overflow = dayItems.length - 3;

          return (
            <div
              key={i}
              onClick={() => onDayClick(cell)}
              className={cn(
                'border-b border-r border-border p-1 cursor-pointer hover:bg-accent/40 transition-colors overflow-hidden',
                !isCurrentMonth && 'bg-muted/20',
                isToday && 'bg-primary/5 ring-1 ring-inset ring-primary/20',
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={cn(
                    'text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full',
                    isToday && 'bg-primary text-primary-foreground',
                    !isCurrentMonth && 'text-muted-foreground/50',
                    !isToday && isCurrentMonth && 'text-foreground',
                  )}
                >
                  {cell.getDate()}
                </span>
              </div>
              <div className="space-y-0.5">
                {visible.map((item) => (
                  <EventChip key={item.id} item={item} onClick={() => onEventClick(item)} compact />
                ))}
                {overflow > 0 && (
                  <p className="text-[10px] text-muted-foreground px-1">+{overflow} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Time Grid (shared by Day + Week) ────────────────────────────────────────

function TimeGrid({ days, items, onEventClick }: {
  days: Date[];
  items: CalendarItem[];
  onEventClick: (item: CalendarItem) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const today = new Date();
  const [nowMinute, setNowMinute] = useState(today.getHours() * 60 + today.getMinutes());

  useEffect(() => {
    const timer = setInterval(() => {
      const n = new Date();
      setNowMinute(n.getHours() * 60 + n.getMinutes());
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const scrollTo = Math.max(0, (nowMinute / 60 - 2) * HOUR_HEIGHT);
      containerRef.current.scrollTop = scrollTo;
    }
  }, []);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const allDayItems: CalendarItem[] = [];
  const timedItems: CalendarItem[] = [];

  for (const day of days) {
    const dayItems = itemsForDay(items, day);
    for (const item of dayItems) {
      if (item.allDay) { if (!allDayItems.find((x) => x.id === item.id)) allDayItems.push(item); }
      else { if (!timedItems.find((x) => x.id === item.id)) timedItems.push(item); }
    }
  }

  const todayColIdx = days.findIndex((d) => sameDay(d, today));

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* All-day row */}
      {allDayItems.length > 0 && (
        <div className="border-b border-border bg-muted/10 flex">
          <div className="w-14 flex-shrink-0 border-r border-border px-1 py-1 text-[10px] text-muted-foreground text-right">
            all-day
          </div>
          <div className="flex-1 grid p-1 gap-0.5" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
            {days.map((day, di) => {
              const dayAllDay = allDayItems.filter((item) => {
                const s = item.start.slice(0, 10);
                const e = item.end.slice(0, 10);
                const d = toDateStr(day);
                return s <= d && e >= d;
              });
              return (
                <div key={di} className="space-y-0.5 min-h-[20px]">
                  {dayAllDay.map((item) => (
                    <EventChip key={item.id} item={item} onClick={() => onEventClick(item)} compact />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scrollable time grid */}
      <div ref={containerRef} className="flex-1 overflow-y-auto relative">
        <div className="relative flex" style={{ height: TOTAL_HEIGHT }}>
          {/* Hour labels */}
          <div className="w-14 flex-shrink-0 border-r border-border">
            {hours.map((h) => (
              <div key={h} className="relative" style={{ height: HOUR_HEIGHT }}>
                {h > 0 && (
                  <span className="absolute -top-2.5 right-2 text-[10px] text-muted-foreground select-none">
                    {h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Day columns */}
          <div className="flex-1 grid relative" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
            {/* Hour lines */}
            <div className="absolute inset-0 pointer-events-none">
              {hours.map((h) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-border/50"
                  style={{ top: h * HOUR_HEIGHT }}
                />
              ))}
            </div>

            {/* Current time line */}
            {todayColIdx >= 0 && (
              <div
                className="absolute pointer-events-none z-20"
                style={{
                  top: (nowMinute / 60) * HOUR_HEIGHT,
                  left: `calc(${(todayColIdx / days.length) * 100}%)`,
                  width: `calc(${(1 / days.length) * 100}%)`,
                }}
              >
                <div className="relative flex items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 -ml-1.5 flex-shrink-0" />
                  <div className="flex-1 h-px bg-red-500" />
                </div>
              </div>
            )}

            {/* Events per day column */}
            {days.map((day, di) => {
              const colTimedItems = timedItems.filter((item) => {
                const s = item.start.slice(0, 10);
                const e = item.end.slice(0, 10);
                const d = toDateStr(day);
                return s <= d && e >= d;
              });

              return (
                <div key={di} className={cn(
                  'relative border-r border-border/30 last:border-r-0',
                  sameDay(day, today) && 'bg-primary/[0.03]',
                )}>
                  {colTimedItems.map((item) => {
                    const startMin = minuteOfDay(item.start);
                    const endMin   = Math.max(minuteOfDay(item.end), startMin + 30);
                    const top      = (startMin / 60) * HOUR_HEIGHT;
                    const height   = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 20);

                    return (
                      <button
                        key={item.id}
                        onClick={() => onEventClick(item)}
                        className="absolute left-0.5 right-0.5 rounded text-white text-xs px-1.5 py-0.5 text-left overflow-hidden hover:opacity-90 transition-opacity cursor-pointer z-10"
                        style={{ top, height, backgroundColor: item.color }}
                        title={item.title}
                      >
                        <div className="font-medium truncate">{item.title}</div>
                        <div className="opacity-80 truncate">{formatTime(item.start)}</div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Week View ────────────────────────────────────────────────────────────────

function WeekView({ currentDate, items, onEventClick }: {
  currentDate: Date;
  items: CalendarItem[];
  onEventClick: (item: CalendarItem) => void;
}) {
  const today = new Date();
  const weekStart = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const DAY_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Day headers */}
      <div className="flex border-b border-border bg-background flex-shrink-0">
        <div className="w-14 flex-shrink-0 border-r border-border" />
        {days.map((day, i) => {
          const isToday = sameDay(day, today);
          return (
            <div key={i} className="flex-1 text-center py-2 border-r border-border/50 last:border-r-0">
              <div className="text-[11px] font-semibold text-muted-foreground">{DAY_SHORT[i]}</div>
              <div
                className={cn(
                  'text-lg font-semibold mx-auto w-9 h-9 flex items-center justify-center rounded-full',
                  isToday ? 'bg-primary text-primary-foreground' : 'text-foreground',
                )}
              >
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>
      <TimeGrid days={days} items={items} onEventClick={onEventClick} />
    </div>
  );
}

// ─── Day View ─────────────────────────────────────────────────────────────────

function DayView({ currentDate, items, onEventClick }: {
  currentDate: Date;
  items: CalendarItem[];
  onEventClick: (item: CalendarItem) => void;
}) {
  const today = new Date();
  const isToday = sameDay(currentDate, today);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Day header */}
      <div className="flex border-b border-border bg-background flex-shrink-0">
        <div className="w-14 flex-shrink-0 border-r border-border" />
        <div className="flex-1 text-center py-3">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {currentDate.toLocaleDateString('en-US', { weekday: 'long' })}
          </div>
          <div
            className={cn(
              'text-2xl font-bold mx-auto w-12 h-12 flex items-center justify-center rounded-full',
              isToday ? 'bg-primary text-primary-foreground' : 'text-foreground',
            )}
          >
            {currentDate.getDate()}
          </div>
          {isToday && <div className="text-xs text-muted-foreground mt-0.5">Today</div>}
        </div>
      </div>
      <TimeGrid days={[currentDate]} items={items} onEventClick={onEventClick} />
    </div>
  );
}

// ─── Agenda View ──────────────────────────────────────────────────────────────

function AgendaView({ items, onEventClick }: {
  items: CalendarItem[];
  onEventClick: (item: CalendarItem) => void;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days: Date[] = [];
  for (let i = 0; i < 60; i++) days.push(addDays(today, i));

  const grouped = days
    .map((day) => ({ day, items: itemsForDay(items, day) }))
    .filter((g) => g.items.length > 0);

  if (grouped.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center space-y-2">
          <Calendar className="h-12 w-12 mx-auto opacity-30" />
          <p className="text-sm">No upcoming events in the next 60 days</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {grouped.map(({ day, items: dayItems }) => (
        <div key={toDateStr(day)}>
          <div className="flex items-center gap-3 mb-2">
            <div className={cn(
              'text-center w-12',
              sameDay(day, new Date()) && 'text-primary',
            )}>
              <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                {day.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className={cn(
                'text-2xl font-bold leading-none',
                sameDay(day, new Date()) ? 'text-primary' : 'text-foreground',
              )}>
                {day.getDate()}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {day.toLocaleDateString('en-US', { month: 'short' })}
              </div>
            </div>
            <div className="flex-1 space-y-1.5">
              {dayItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onEventClick(item)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/40 transition-colors text-left"
                >
                  <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{item.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.allDay ? 'All day' : `${formatTime(item.start)} – ${formatTime(item.end)}`}
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                    {item.type}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Event Detail Modal ───────────────────────────────────────────────────────

function EventDetailModal({ item, onClose, onDelete }: {
  item: CalendarItem;
  onClose: () => void;
  onDelete: (id: string) => void;
}) {
  const { has } = usePermission();

  const typeIcon = <Calendar className="h-4 w-4" />;
  const typeBadgeClass = 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-1 self-stretch rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: item.color }} />
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-semibold leading-tight">{item.title}</DialogTitle>
              <span className={cn('inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium mt-1 capitalize', typeBadgeClass)}>
                {typeIcon} {item.type}
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span>
              {item.allDay
                ? `${item.start.slice(0, 10)} – ${item.end.slice(0, 10)}`
                : `${formatTime(item.start)} – ${formatTime(item.end)}`}
            </span>
          </div>

          {!!item.metadata.description && (
            <p className="text-foreground">{String(item.metadata.description)}</p>
          )}

          {!!item.metadata.location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span>{String(item.metadata.location)}</span>
            </div>
          )}

          {item.status && (
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="capitalize text-muted-foreground">{item.status.replace(/_/g, ' ')}</span>
            </div>
          )}
        </div>

        {item.type === 'event' && has(PERMISSIONS.CALENDAR_DELETE) && (
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { onDelete(item.sourceId); onClose(); }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Delete
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Create Event Modal ───────────────────────────────────────────────────────

const COLOR_PRESETS = [
  '#0f766e', '#6366f1', '#d97706', '#dc2626', '#7c3aed', '#0284c7',
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  const period = h < 12 ? 'AM' : 'PM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { label: `${displayH}:${m} ${period}`, value: `${String(h).padStart(2, '0')}:${m}` };
});

function CreateEventModal({ defaultDate, onClose, onCreated }: {
  defaultDate: Date;
  onClose: () => void;
  onCreated: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle]           = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate]             = useState(toDateStr(defaultDate));
  const [dateOpen, setDateOpen]     = useState(false);
  const [startTime, setStartTime]   = useState('09:00');
  const [endTime, setEndTime]       = useState('10:00');
  const [color, setColor]           = useState(COLOR_PRESETS[0]);
  const [location, setLocation]     = useState('');
  const [visibility, setVisibility] = useState<'private' | 'team' | 'all'>('private');

  const mutation = useMutation({
    mutationFn: (input: CreateCalendarEventInput) => calendarService.createEvent(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-items'] });
      toast.success('Event created');
      onCreated();
    },
    onError: () => toast.error('Failed to create event'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required'); return; }
    mutation.mutate({
      title: title.trim(),
      description: description || null,
      start_at: `${date}T${startTime}:00Z`,
      end_at: `${date}T${endTime}:00Z`,
      all_day: false,
      color,
      location: location || null,
      is_reminder: false,
      visibility,
    });
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <CalendarPlus className="h-4 w-4 text-primary" />
            </div>
            New Event
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-title" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Title</Label>
            <Input
              id="ev-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's the event?"
              autoFocus
              className="h-10"
            />
          </div>

          {/* Date & Time row */}
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Date</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'w-full h-9 rounded-md border border-input bg-background px-3 text-sm text-left',
                      'flex items-center gap-2 hover:bg-accent/40 transition-colors',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                    )}
                  >
                    <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className={date ? 'text-foreground' : 'text-muted-foreground'}>
                      {date
                        ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })
                        : 'Pick a date'}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0">
                  <CalendarPicker
                    mode="single"
                    selected={date ? new Date(date + 'T00:00:00') : undefined}
                    onSelect={(d) => { if (d) { setDate(toDateStr(d)); setDateOpen(false); } }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Start time</Label>
                <Select value={startTime} onValueChange={setStartTime}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-52">
                    {TIME_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">End time</Label>
                <Select value={endTime} onValueChange={setEndTime}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-52">
                    {TIME_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Color picker */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Color</Label>
            <div className="flex gap-2.5">
              {COLOR_PRESETS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setColor(c)}
                  className={cn(
                    'w-8 h-8 rounded-full transition-all duration-150 flex items-center justify-center',
                    color === c ? 'ring-2 ring-offset-2 ring-foreground/50 scale-110' : 'opacity-70 hover:opacity-100 hover:scale-105',
                  )}
                  style={{ backgroundColor: c }}
                >
                  {color === c && <div className="w-2.5 h-2.5 rounded-full bg-white/60" />}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-desc" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</Label>
            <Textarea
              id="ev-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Add details (optional)"
              className="resize-none"
            />
          </div>

          {/* Location + Visibility row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ev-loc" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Location</Label>
              <Input id="ev-loc" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Add location" className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Visibility</Label>
              <Select value={visibility} onValueChange={(v) => setVisibility(v as typeof visibility)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="all">Everyone</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create Event'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function CalendarPage() {
  const { has } = usePermission();
  const queryClient = useQueryClient();

  const [view, setView]                   = useState<CalendarView>('week');
  const [currentDate, setCurrentDate]     = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const [sidebarOpen, setSidebarOpen]     = useState(true);
  const [createOpen, setCreateOpen]       = useState(false);
  const [selectedItem, setSelectedItem]   = useState<CalendarItem | null>(null);
  const [createDefaultDate, setCreateDefaultDate] = useState<Date>(new Date());

  const { start, end } = useMemo(() => {
    const r = getQueryRange(view, currentDate);
    return { start: toDateStr(r.start), end: toDateStr(r.end) };
  }, [view, currentDate]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['calendar-items', start, end],
    queryFn: () => calendarService.getItems({ start_date: start, end_date: end, types: 'events' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => calendarService.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-items'] });
      toast.success('Event deleted');
    },
    onError: () => toast.error('Failed to delete event'),
  });

  const miniCalMonth = useMemo(() => new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), [currentDate]);

  function miniCalPrev() {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }
  function miniCalNext() {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }

  const navigate = useCallback((dir: -1 | 1) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      if (view === 'day')   next.setDate(next.getDate() + dir);
      if (view === 'week')  next.setDate(next.getDate() + 7 * dir);
      if (view === 'month') next.setMonth(next.getMonth() + dir);
      if (view === 'agenda') next.setDate(next.getDate() + 30 * dir);
      return next;
    });
  }, [view]);

  function goToday() {
    const d = new Date(); d.setHours(0,0,0,0); setCurrentDate(d);
  }

  function handleDayClick(d: Date) {
    setCurrentDate(d);
    setView('day');
  }

  function getViewLabel(): string {
    if (view === 'day')    return formatDayFull(currentDate);
    if (view === 'week')   return formatWeekRange(currentDate);
    if (view === 'month')  return formatMonthYear(currentDate);
    return 'Next 60 days';
  }

  return (
    <div className="flex h-full bg-background overflow-hidden -m-3 md:-m-5">
      {/* Left sidebar */}
      <aside className={cn(
        'flex-shrink-0 border-r border-border bg-card/60 backdrop-blur-sm flex flex-col transition-all duration-200 overflow-hidden',
        sidebarOpen ? 'w-72' : 'w-0',
      )}>
        <div className="px-3 pt-3 pb-3 flex-shrink-0">
          {/* Create button */}
          {has(PERMISSIONS.CALENDAR_CREATE) && (
            <Button
              className="w-full justify-start gap-2 mb-3"
              onClick={() => { setCreateDefaultDate(new Date()); setCreateOpen(true); }}
            >
              <Plus className="h-4 w-4" /> Create
            </Button>
          )}

          {/* Mini calendar — nav hidden, custom bottom row */}
          <CalendarPicker
            mode="single"
            selected={currentDate}
            onSelect={(d) => d && setCurrentDate(d)}
            month={miniCalMonth}
            onMonthChange={() => {}}
            hideNavigation
            className="p-0 w-full"
            classNames={{ month_caption: 'hidden' }}
          />

          {/* Bottom navigation row */}
          <div className="flex items-center justify-between mt-1 px-1">
            <button
              onClick={miniCalPrev}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-accent transition-colors text-muted-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-foreground select-none">
              {miniCalMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={miniCalNext}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-accent transition-colors text-muted-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-2 border-b border-border bg-background/80 backdrop-blur-sm flex-shrink-0">
          {/* Left controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSidebarOpen((o) => !o)}
              className="h-8 w-8 flex items-center justify-center rounded hover:bg-accent transition-colors"
            >
              {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>

            <Button variant="outline" size="sm" onClick={goToday}>Today</Button>

            <div className="flex items-center gap-1">
              <button onClick={() => navigate(-1)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-accent transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={() => navigate(1)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-accent transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <span className="font-semibold text-sm hidden sm:block">{getViewLabel()}</span>
          </div>

          {/* View tabs — full-width row on mobile, auto + right-aligned on sm+ */}
          <div className="w-full sm:ml-auto sm:w-auto">
            <Tabs
              tabs={[
                { key: 'day',    label: 'Day' },
                { key: 'week',   label: 'Week' },
                { key: 'month',  label: 'Month' },
                { key: 'agenda', label: 'Agenda' },
              ]}
              active={view}
              onChange={(v) => setView(v as CalendarView)}
            />
          </div>
        </div>

        {/* Loading overlay */}
        {isLoading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-muted-foreground text-sm animate-pulse">Loading calendar…</div>
          </div>
        )}

        {/* Views */}
        {!isLoading && (
          <>
            {view === 'month'  && <MonthView currentDate={currentDate} items={items} onDayClick={handleDayClick} onEventClick={setSelectedItem} />}
            {view === 'week'   && <WeekView  currentDate={currentDate} items={items} onEventClick={setSelectedItem} />}
            {view === 'day'    && <DayView   currentDate={currentDate} items={items} onEventClick={setSelectedItem} />}
            {view === 'agenda' && <AgendaView items={items} onEventClick={setSelectedItem} />}
          </>
        )}
      </div>

      {/* FAB */}
      {has(PERMISSIONS.CALENDAR_CREATE) && (
        <button
          onClick={() => { setCreateDefaultDate(new Date()); setCreateOpen(true); }}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center z-sticky lg:hidden"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}

      {/* Modals */}
      {createOpen && (
        <CreateEventModal
          defaultDate={createDefaultDate}
          onClose={() => setCreateOpen(false)}
          onCreated={() => setCreateOpen(false)}
        />
      )}

      {selectedItem && (
        <EventDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onDelete={(id) => { deleteMutation.mutate(id); setSelectedItem(null); }}
        />
      )}
    </div>
  );
}
